import { useState, useMemo } from 'react';
import SEOMeta from '@/components/feature/SEOMeta';
import SchemaOrg from '@/components/feature/SchemaOrg';
import HeroSection from './components/HeroSection';
import CategoryTabs from './components/CategoryTabs';
import VideoGrid from './components/VideoGrid';
import ArticleSection from './components/ArticleSection';
import FAQSection from './components/FAQSection';
import { allVideos } from '@/mocks/videos';

export default function Home() {
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredVideos = useMemo(() => {
    if (activeCategory === 'all') return allVideos.slice(0, 12);
    return allVideos.filter((v) => v.category === activeCategory);
  }, [activeCategory]);

  const activeCategoryName = useMemo(() => {
    if (activeCategory === 'all') return '全部';
    const cat = allVideos.find((v) => v.category === activeCategory);
    return cat ? (activeCategory === 'movie' ? '电影' : activeCategory === 'tv' ? '电视剧' : activeCategory === 'variety' ? '综艺' : activeCategory === 'anime' ? '动漫' : activeCategory === 'documentary' ? '纪录片' : '短视频') : '';
  }, [activeCategory]);

  return (
    <>
      <SEOMeta
        title="芭乐视频 - 91视频、97视频网站替代 | 免费在线观看数字视频高清不缓冲"
        description="芭乐视频为喜爱91视频、97视频的用户提供高清免费在线观看新选择，涵盖数字视频平台的热门资源，急速加载不缓冲，电影电视剧全集一键畅享。"
        keywords="芭乐视频,91视频,97视频,91在线视频,数字视频平台,芭乐视频免费观看,免费视频网站,高清在线观看,不缓冲,电视剧全集,最新电影"
        canonicalUrl="https://balevideo.com/"
        ogUrl="https://balevideo.com/"
      />

      <SchemaOrg type="Organization" data={{
        name: '芭乐视频',
        url: 'https://balevideo.com/',
        description: '芭乐视频是新一代高清在线视频平台，提供电影、电视剧、综艺、动漫等海量数字视频资源免费在线观看。',
        sameAs: ['https://balevideo.com/'],
      }} />

      <SchemaOrg type="WebSite" data={{
        name: '芭乐视频',
        url: 'https://balevideo.com/',
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://balevideo.com/search?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      }} />

      <SchemaOrg type="FAQPage" data={{
        mainEntity: [
          {
            '@type': 'Question',
            name: '芭乐视频和91视频、97视频有什么区别？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '芭乐视频是91视频和97视频的优质替代平台，拥有更先进的CDN加速技术，实现4K视频秒开不缓冲；更智能的推荐算法；更清爽的界面设计；涵盖电影、电视剧、综艺、动漫等全品类内容。',
            },
          },
          {
            '@type': 'Question',
            name: '芭乐视频真的免费吗？需要注册会员吗？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '芭乐视频平台上的绝大部分内容都可以免费在线观看，无需注册即可享受高清观影体验。',
            },
          },
          {
            '@type': 'Question',
            name: '为什么芭乐视频播放不缓冲？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '芭乐视频采用了全国超过500个CDN加速节点、自适应码率技术、H.265高效编码和智能预加载算法，确保流畅播放。',
            },
          },
          {
            '@type': 'Question',
            name: '支持哪些设备观看？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '芭乐视频全面支持PC电脑、平板、手机等多种设备，覆盖Windows、macOS、iOS、Android等主流操作系统。',
            },
          },
          {
            '@type': 'Question',
            name: '视频画质怎么样？支持4K吗？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '芭乐视频所有视频内容均支持1080P高清画质，大部分热门影片和剧集支持4K超清画质。',
            },
          },
          {
            '@type': 'Question',
            name: '如何反馈问题或提出建议？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '你可以通过关于我们页面的联系表单提交意见和建议，芭乐视频团队会认真对待每一条反馈。',
            },
          },
        ],
      }} />

      <HeroSection />

      <section className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
              {activeCategory === 'all' ? '热门推荐' : activeCategoryName}
            </h2>
            <p className="text-sm text-foreground-600 mt-1">精选优质内容，发现你的下一部最爱</p>
          </div>
          <CategoryTabs active={activeCategory} onChange={setActiveCategory} />
        </div>
        <VideoGrid videos={filteredVideos} />
      </section>

      <ArticleSection />
      <FAQSection />

      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://balevideo.com/' },
        ],
      }} />
    </>
  );
}